/* ============================================================
   SIWSA Solar Kit page — interactivity
   ============================================================
   Handles:
     - +/- steppers with cap enforcement (and info popup at cap)
     - Rate slider live-recomputes savings
     - Stepper changes scale production chart, savings, price
     - Cart form add-to-cart with kit + extras as separate items
     - Tabs (specs / downloads / description)
     - Info popups (cap explanations + savings/production disclaimers)
   ============================================================ */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('[data-kit-page]');
    if (!root) return;

    var state = {
      basePanelQty: parseInt(root.dataset.basePanelQty || '0', 10),
      baseBatteryQty: parseInt(root.dataset.baseBatteryQty || '0', 10),
      extraPanels: 0,
      extraBatteries: 0,
      maxExtraPanels: parseInt(root.dataset.extraPanelMax || '0', 10),
      maxExtraBatteries: parseInt(root.dataset.extraBatteryMax || '0', 10),
      baseKitCents: parseInt(root.dataset.kitBasePriceCents || '0', 10),
      extraPanelCents: parseInt(root.dataset.extraPanelUnitCents || '0', 10),
      extraBatteryCents: parseInt(root.dataset.extraBatteryUnitCents || '0', 10),
      extraPanelVariantId: root.dataset.extraPanelVariantId || '',
      extraBatteryVariantId: root.dataset.extraBatteryVariantId || '',
      baselineKwh: parseFloat(root.dataset.baselineKwh || '0'),
      baselineSavings: parseFloat(root.dataset.baselineSavings || '0'),
      baselinePayback: parseFloat(root.dataset.baselinePayback || '0'),
      baselineRate: parseFloat(root.dataset.baselineRate || '3'),
      currentRate: parseFloat(root.dataset.baselineRate || '3')
    };

    // ----- Utility ------------------------------------------------
    function formatRand(amount) {
      // en-GB locale: comma thousands + dot decimal, matches shop money_format
      // (e.g. "Save R 5,350.00 vs. buying separately").
      return 'R ' + amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    function formatCents(cents) { return formatRand(cents / 100); }

    // ----- Compute scaled values ---------------------------------
    function panelMultiplier() {
      if (!state.basePanelQty) return 1;
      return (state.basePanelQty + state.extraPanels) / state.basePanelQty;
    }
    function rateMultiplier() {
      if (!state.baselineRate) return 1;
      return state.currentRate / state.baselineRate;
    }

    function recompute() {
      var pm = panelMultiplier();
      var rm = rateMultiplier();

      // Production scales with panel count only (batteries don't generate)
      var scaledKwh = state.baselineKwh * pm;

      // Savings scales with panels (more production) and rate (R/kWh)
      var scaledSavings = state.baselineSavings * pm * rm;

      // Payback scales inversely — more savings = shorter payback
      // but extras also add cost so payback = (kit + extras) / savings
      var totalCostCents = state.baseKitCents
        + state.extraPanels * state.extraPanelCents
        + state.extraBatteries * state.extraBatteryCents;
      var scaledPayback = (totalCostCents / 100) / scaledSavings;
      if (!isFinite(scaledPayback) || scaledPayback < 0) scaledPayback = state.baselinePayback;

      // Price display
      var priceEl = root.querySelector('[data-kit-price-display]');
      if (priceEl) priceEl.textContent = formatCents(totalCostCents);

      // Production stats
      setText('[data-kit-annual-display]', Math.round(scaledKwh).toLocaleString('en-ZA') + ' kWh');
      setText('[data-kit-daily-display]', (scaledKwh / 365).toFixed(1) + ' kWh');

      // Bar tooltip text (heights stay as Liquid set them — relative
      // distribution doesn't change when steppers are adjusted)
      var bars = root.querySelectorAll('[data-kit-bar]');
      bars.forEach(function (bar) {
        var ratio = parseFloat(bar.dataset.ratio || '0');
        var monthly = scaledKwh * ratio;
        bar.title = Math.round(monthly).toLocaleString('en-ZA') + ' kWh';
      });

      // Savings stats
      setText('[data-kit-savings-display]', formatRand(scaledSavings));
      setText('[data-kit-payback-display]', scaledPayback.toFixed(1));
    }

    function setText(selector, value) {
      var el = root.querySelector(selector);
      if (el) el.textContent = value;
    }

    // ----- Steppers ----------------------------------------------
    function bindStepper(kind, max) {
      var wrap = root.querySelector('[data-kit-stepper="' + kind + '"]');
      if (!wrap) return;
      var countEl = wrap.querySelector('[data-kit-stepper-count]');
      var incBtn = wrap.querySelector('[data-kit-stepper-inc]');
      var decBtn = wrap.querySelector('[data-kit-stepper-dec]');
      function refresh() {
        var current = (kind === 'panel') ? state.extraPanels : state.extraBatteries;
        countEl.textContent = current;
        decBtn.disabled = (current <= 0);
        incBtn.disabled = (current >= max);
      }
      incBtn.addEventListener('click', function () {
        var current = (kind === 'panel') ? state.extraPanels : state.extraBatteries;
        if (current >= max) {
          openPopup('cap-' + kind);
          return;
        }
        if (kind === 'panel') state.extraPanels++; else state.extraBatteries++;
        refresh();
        recompute();
      });
      decBtn.addEventListener('click', function () {
        var current = (kind === 'panel') ? state.extraPanels : state.extraBatteries;
        if (current <= 0) return;
        if (kind === 'panel') state.extraPanels--; else state.extraBatteries--;
        refresh();
        recompute();
      });
      refresh();
    }

    bindStepper('panel', state.maxExtraPanels);
    bindStepper('battery', state.maxExtraBatteries);

    // ----- Rate slider --------------------------------------------
    var slider = root.querySelector('[data-kit-rate]');
    if (slider) {
      var output = root.querySelector('[data-kit-rate-display]');
      function syncRate() {
        state.currentRate = parseFloat(slider.value);
        if (output) output.textContent = 'R ' + state.currentRate.toFixed(2);
        recompute();
      }
      slider.addEventListener('input', syncRate);
      syncRate();
    }

    // ----- Popups -------------------------------------------------
    function openPopup(name) {
      var popup = root.querySelector('[data-kit-popup="' + name + '"]');
      if (popup) {
        popup.hidden = false;
        document.body.style.overflow = 'hidden';
      }
    }
    function closePopup(popup) {
      popup.hidden = true;
      document.body.style.overflow = '';
    }
    root.querySelectorAll('[data-kit-info-popup]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openPopup(btn.dataset.kitInfoPopup);
      });
    });
    root.querySelectorAll('[data-kit-popup-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        var popup = el.closest('[data-kit-popup]');
        if (popup) closePopup(popup);
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        root.querySelectorAll('[data-kit-popup]').forEach(function (popup) {
          if (!popup.hidden) closePopup(popup);
        });
      }
    });

    // ----- Tabs ---------------------------------------------------
    var tabs = root.querySelector('[data-kit-tabs]');
    if (tabs) {
      var tabBtns = tabs.querySelectorAll('[data-kit-tab]');
      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.dataset.kitTab;
          tabBtns.forEach(function (b) { b.classList.toggle('is-active', b === btn); });
          tabs.querySelectorAll('[data-kit-pane]').forEach(function (pane) {
            pane.classList.toggle('is-active', pane.dataset.kitPane === target);
          });
        });
      });
    }

    // ----- Cart form (kit + extras as separate line items) -------
    var cartForm = root.querySelector('[data-kit-cart-form]');
    if (cartForm) {
      cartForm.addEventListener('submit', function (e) {
        var hasExtras = (state.extraPanels > 0 && state.extraPanelVariantId)
                     || (state.extraBatteries > 0 && state.extraBatteryVariantId);
        if (!hasExtras) return; // Plain submit handles the kit alone

        // Otherwise we intercept and use the cart/add.js batch endpoint
        e.preventDefault();
        var items = [{ id: cartForm.elements['id'].value, quantity: 1 }];
        if (state.extraPanels > 0 && state.extraPanelVariantId) {
          items.push({ id: state.extraPanelVariantId, quantity: state.extraPanels });
        }
        if (state.extraBatteries > 0 && state.extraBatteryVariantId) {
          items.push({ id: state.extraBatteryVariantId, quantity: state.extraBatteries });
        }
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        }).then(function (r) {
          if (r.ok) window.location.href = '/cart';
          else r.json().then(function (j) { alert(j.description || 'Could not add to cart.'); });
        }).catch(function () {
          alert('Network error adding to cart. Please try again.');
        });
      });
    }

    // Initial render
    recompute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
